/**
 * Variant options on product cards (snippets/product-card.liquid, and the
 * cards search-page.js builds): colour swatches and size chips over the
 * bottom of the image.
 *
 * - A swatch's colour is the option value's own swatch when the admin has
 *   one (the Liquid sets --swatch); otherwise the name is resolved here —
 *   "Heather Gray", "Indy Red" — and a name that resolves to nothing shows
 *   its first letter instead of pretending.
 * - Hovering (or focusing) a swatch shows that variant's first image in the
 *   card, and leaving puts the product's own image back (Lucas, 2026-10-12).
 * - Clicking either kind is an ordinary link to the product; the variant's
 *   id rides on the link (data-variant) and is added as ?variant= the
 *   moment the link is followed, so the page loads with it selected.
 *   Not in the markup: Shopify's collection-attribution script rewrites
 *   product links after load (_pos, _fid, _ss) and mangles one that
 *   already has a query. Adding it on mousedown/click keeps the
 *   attribution and works for middle and modified clicks.
 *
 * Cards that arrive later — fetched rows, a re-rendered grid — are painted
 * by a mutation observer, as fits-badges.js does.
 */
(function () {
  'use strict';

  const COLORS = {
    black: '#111111', white: '#ffffff', gray: '#9a9a9a', grey: '#9a9a9a', 'heather gray': '#a8a8a8', 'heather grey': '#a8a8a8',
    charcoal: '#444444', silver: '#c0c0c0', graphite: '#555555', 'gun metal': '#5a5f66', gunmetal: '#5a5f66',
    red: '#c62828', 'indy red': '#c62828', crimson: '#b0173a', maroon: '#7a1f2b', burgundy: '#7a1f2b',
    blue: '#1e5bb8', navy: '#1b2a4a', 'light blue': '#7fb3e6', 'royal blue': '#2b4fd6', 'sky blue': '#7fb3e6',
    orange: '#ff6a00', green: '#2e7d32', lime: '#9ccc3d', olive: '#6b7a3a', 'army green': '#5a6b3a', camo: '#5a6b3a',
    yellow: '#f2c200', gold: '#c9a227', brown: '#6d4c2f', tan: '#d2b48c', beige: '#e6d5b8', khaki: '#c3b091', sand: '#d8c8a0',
    pink: '#e91e63', purple: '#6a1b9a', teal: '#008080', turquoise: '#30c3c3', cream: '#f3ecd8', ivory: '#f3ecd8',
    titanium: '#8b8b8b', stealth: '#3a3a3a',
  };
  const NAMES = Object.keys(COLORS).sort((a, b) => b.length - a.length);

  /** A CSS colour for a colour name, or null. Longest known name wins. */
  function colorFor(name) {
    const n = String(name || '').toLowerCase().trim();
    if (!n) return null;
    if (COLORS[n]) return COLORS[n];
    for (const key of NAMES) if (n.includes(key)) return COLORS[key];
    return null;
  }

  function paint(root) {
    (root || document).querySelectorAll('[data-card-swatch]:not([data-painted])').forEach((swatch) => {
      swatch.dataset.painted = '';
      if (swatch.style.getPropertyValue('--swatch')) return;
      const color = colorFor(swatch.dataset.color);
      if (color) swatch.style.setProperty('--swatch', color);
      else swatch.textContent = String(swatch.dataset.color || '?').trim().slice(0, 1).toUpperCase();
    });
  }

  function primaryOf(swatch) {
    const card = swatch.closest('.product-card');
    return { card, img: card && card.querySelector('.product-card__image--primary') };
  }

  function show(swatch) {
    const { card, img } = primaryOf(swatch);
    if (!card || !img || !swatch.dataset.image) return;
    if (img.dataset.ownSrc === undefined) {
      img.dataset.ownSrc = img.getAttribute('src') || '';
      img.dataset.ownSrcset = img.getAttribute('srcset') || '';
    }
    img.removeAttribute('srcset');
    img.src = swatch.dataset.image;
    card.classList.add('product-card--picking');
  }

  function restore(swatch) {
    const { card, img } = primaryOf(swatch);
    if (!card || !img) return;
    if (img.dataset.ownSrc !== undefined) {
      img.src = img.dataset.ownSrc;
      if (img.dataset.ownSrcset) img.setAttribute('srcset', img.dataset.ownSrcset);
    }
    card.classList.remove('product-card--picking');
  }

  function leaving(event) {
    const swatch = event.target.closest && event.target.closest('[data-card-swatch]');
    if (!swatch) return;
    const to = event.relatedTarget;
    if (to && to.closest && to.closest('[data-card-swatch]') && to.closest('.product-card') === swatch.closest('.product-card')) return;
    restore(swatch);
  }

  /** The link's href with the chosen variant, whatever else is on it by now. */
  function withVariant(link) {
    if (!link.dataset.variant) return;
    try {
      const url = new URL(link.getAttribute('href') || '', window.location.origin);
      url.searchParams.set('variant', link.dataset.variant);
      link.setAttribute('href', url.pathname + url.search + url.hash);
    } catch { /* leave the link as it is */ }
  }

  function boot() {
    paint(document);
    for (const type of ['mousedown', 'click', 'keydown']) {
      document.addEventListener(type, (event) => {
        if (type === 'keydown' && event.key !== 'Enter') return;
        const link = event.target.closest && event.target.closest('[data-card-chip][data-variant], [data-card-swatch][data-variant]');
        if (link) withVariant(link);
      }, true);
    }
    document.addEventListener('mouseover', (event) => {
      const swatch = event.target.closest && event.target.closest('[data-card-swatch]');
      if (swatch) show(swatch);
    });
    document.addEventListener('mouseout', leaving);
    document.addEventListener('focusin', (event) => {
      const swatch = event.target.closest && event.target.closest('[data-card-swatch]');
      if (swatch) show(swatch);
    });
    document.addEventListener('focusout', leaving);
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((m) => m.addedNodes.length)) paint(document);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.CardOptions = { colorFor, paint, show, restore, withVariant };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
